import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

const VEHICLE_TYPES = ["Sedan", "SUV", "Pickup Truck", "Commercial Vehicle / Van", "Motorcycle", "Other"];
const TRANSPORT_TYPES = ["Open Transport", "Enclosed Transport"];

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700";
const labelCls = "block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5";

export const QuoteBox = () => {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [vehicleType, setVehicleType] = useState(VEHICLE_TYPES[0]);
  const [transportType, setTransportType] = useState(TRANSPORT_TYPES[0]);

  const isZip = (z) => /^\d{5}$/.test(z.trim());

  const submit = (e) => {
    e.preventDefault();
    if (!isZip(origin) || !isZip(destination)) {
      toast.error("Please enter valid 5-digit origin and destination ZIP codes.");
      return;
    }
    const subject = `Transport Quote: ${origin} → ${destination} (${vehicleType})`;
    const message =
      `I'd like a transport quote.\n\n` +
      `Pickup ZIP: ${origin}\n` +
      `Delivery ZIP: ${destination}\n` +
      `Vehicle Type: ${vehicleType}\n` +
      `Transport Type: ${transportType}`;
    navigate(
      `/contact?type=${encodeURIComponent("Transportation Quote")}` +
      `&subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`
    );
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white shadow-sm" data-testid="home-quote-box">
      <div className="bg-slate-900 px-6 py-4 rounded-t-md">
        <h2 className="text-white font-bold">Request a Transport Quote</h2>
        <p className="text-slate-400 text-sm mt-0.5">Enter pickup and delivery details to get started.</p>
      </div>
      <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
        <div>
          <label className={labelCls}>Origin ZIP</label>
          <input
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            inputMode="numeric"
            maxLength={5}
            placeholder="33316"
            className={inputCls}
            data-testid="quote-origin-input"
          />
        </div>
        <div>
          <label className={labelCls}>Destination ZIP</label>
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            inputMode="numeric"
            maxLength={5}
            placeholder="77002"
            className={inputCls}
            data-testid="quote-destination-input"
          />
        </div>
        <div>
          <label className={labelCls}>Vehicle Type</label>
          <select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} className={inputCls} data-testid="quote-vehicle-type-select">
            {VEHICLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Transport Type</label>
          <select value={transportType} onChange={(e) => setTransportType(e.target.value)} className={inputCls} data-testid="quote-transport-type-select">
            {TRANSPORT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button
          type="submit"
          data-testid="quote-box-submit-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-900 transition-colors h-[42px]"
        >
          Get Quote <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};
